import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, FileText, DownloadCloud } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function AdminResearch() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", summary: "", author: "", language: "ar" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const t = useMemo(() => ({
    eyebrow: isAr ? "إدارة الأبحاث" : "Manage Research",
    title: isAr ? "ارفع أبحاثاً ومذكرات قانونية." : "Publish research papers and legal memos.",
    lead: isAr
      ? "الأبحاث المرفوعة تظهر في صفحة «الأبحاث» العامة، ويستند إليها سامي ضمن إطار تحليله."
      : "Uploaded research is published on the public Research page and is referenced by Sami in his analysis.",
    upload: isAr ? "رفع بحث جديد" : "Upload research",
    fTitle: isAr ? "عنوان البحث" : "Title",
    fSummary: isAr ? "الملخص (اختياري)" : "Summary (optional)",
    fAuthor: isAr ? "المؤلف (اختياري)" : "Author (optional)",
    fLang: isAr ? "اللغة" : "Language",
    fFile: isAr ? "الملف (PDF أو TXT)" : "File (PDF or TXT)",
    submit: isAr ? "رفع" : "Upload",
    library: isAr ? "الأبحاث الحالية" : "Current research",
    libraryEmpty: isAr ? "لم تُرفع أبحاث بعد." : "No research uploaded yet.",
    delete: isAr ? "حذف" : "Delete",
    confirmDelete: isAr ? "حذف هذا البحث؟" : "Delete this research?",
    uploaded: isAr ? "تم الرفع" : "Uploaded",
    err: isAr ? "حدث خطأ. حاول مرة أخرى." : "Something went wrong.",
    syncTitle: isAr ? "استيراد الأبحاث من بيئة أخرى" : "Import research from another environment",
    syncLead: isAr
      ? "ألصق رابط /api/research/export الخاص بـ Preview لنقل كل الأبحاث إلى هذه البيئة. الأبحاث بنفس العنوان تُتجاوز."
      : "Paste the /api/research/export URL of your Preview deployment to copy all papers here. Same title is skipped.",
    syncPlaceholder: "https://example.com/api/research/export",
    syncBtn: isAr ? "استيراد الآن" : "Import now",
    syncing: isAr ? "جارٍ الاستيراد..." : "Importing…",
    syncOk: (n) => isAr ? `تم استيراد ${n} بحث.` : `Imported ${n} papers.`,
  }), [isAr]);

  const load = useCallback(() => {
    api.get("/research").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const onUpload = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("summary", form.summary.trim());
      fd.append("author", form.author.trim());
      fd.append("language", form.language);
      fd.append("file", file);
      await api.post("/admin/research/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success(t.uploaded);
      setForm({ title: "", summary: "", author: "", language: form.language });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await api.delete(`/admin/research/${id}`);
      load();
    } catch {
      toast.error(t.err);
    }
  };

  const onImport = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await api.post(
        "/admin/research/import-from-url",
        { url: importUrl.trim(), overwrite: false },
        { timeout: 240000 }
      );
      toast.success(t.syncOk(data.inserted ?? 0));
      setImportUrl("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="admin-research-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      <form
        onSubmit={onImport}
        className="border border-slate-200 bg-slate-50 p-5 lg:p-6 mb-8 grid md:grid-cols-[1fr_auto] gap-3 items-start"
        data-testid="research-import-form"
      >
        <div className="md:col-span-2 flex items-center gap-2 mb-1">
          <DownloadCloud className="w-4 h-4 text-slate-500" />
          <p className="eyebrow">{t.syncTitle}</p>
        </div>
        <div className="md:col-span-2 -mt-1 mb-1">
          <p className="text-sm text-slate-600 leading-relaxed">{t.syncLead}</p>
        </div>
        <Input
          type="url"
          value={importUrl}
          onChange={(e) => setImportUrl(e.target.value)}
          placeholder={t.syncPlaceholder}
          data-testid="research-import-url"
          className="rounded-sm h-11"
          dir="ltr"
        />
        <Button
          type="submit"
          disabled={importing || !importUrl.trim()}
          data-testid="research-import-submit"
          className="rounded-sm h-11 bg-slate-900 hover:bg-slate-800 text-white whitespace-nowrap"
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : t.syncBtn}
        </Button>
      </form>

      <div className="grid lg:grid-cols-12 gap-8">
        <form
          onSubmit={onUpload}
          className="lg:col-span-5 border border-slate-200 bg-white p-6 lg:p-8 space-y-5 self-start"
          data-testid="research-upload-form"
        >
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-slate-500" />
            <p className="eyebrow">{t.upload}</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.fTitle}</Label>
            <Input
              dir="auto" required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="research-title"
              className="mt-2 rounded-sm h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.fAuthor}</Label>
            <Input
              dir="auto"
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              data-testid="research-author"
              className="mt-2 rounded-sm h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.fSummary}</Label>
            <Textarea
              dir="auto" rows={3}
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              data-testid="research-summary"
              className="mt-2 rounded-sm"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.fLang}</Label>
            <Select
              value={form.language}
              onValueChange={(v) => setForm({ ...form, language: v })}
            >
              <SelectTrigger data-testid="research-language" className="mt-2 rounded-sm h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ar">{isAr ? "العربية" : "Arabic"}</SelectItem>
                <SelectItem value="en">{isAr ? "الإنجليزية" : "English"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.fFile}</Label>
            <input
              ref={fileRef}
              type="file" required
              accept=".pdf,.txt,.md"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="research-file"
              className="mt-2 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-slate-300 file:bg-white file:text-slate-700 hover:file:border-slate-900"
            />
          </div>
          <Button
            type="submit" disabled={uploading || !file}
            data-testid="research-submit"
            className="rounded-sm h-12 w-full bg-slate-900 hover:bg-slate-800 text-white"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.submit}
          </Button>
        </form>

        <div className="lg:col-span-7">
          <div className="border border-slate-200 bg-white">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              <p className="eyebrow">
                {t.library} {items.length > 0 && <span className="ms-2 text-slate-400">· {items.length}</span>}
              </p>
            </div>
            {items.length === 0 ? (
              <div className="p-10 text-center text-slate-500 text-sm">{t.libraryEmpty}</div>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-[760px] overflow-auto">
                {items.map((r) => (
                  <li key={r.id} data-testid={`admin-research-row-${r.id}`} className="flex items-start justify-between gap-4 p-5">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{r.title}</p>
                      {r.author && (
                        <p className="text-xs text-slate-500 mt-0.5">{r.author}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1.5 truncate">
                        {r.language === "ar" ? "AR" : "EN"} · {r.filename}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onDelete(r.id)}
                      data-testid={`admin-research-delete-${r.id}`}
                      className="text-slate-400 hover:text-red-600 transition-colors shrink-0"
                      aria-label={t.delete}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
